/**
 * Shared footer for the D.I.L.A. storefront pages.
 * - "About D.I.L.A." manifesto (mirrors the Matrix homepage copy)
 * - Email catcher for future Design Studio releases
 * - "Shop the full collection" links to the in-app /dila collection (stays in PRSVR)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Check } from 'lucide-react';
import { subscribeNewsletter } from '../services/api';
import { toast } from 'sonner';

export const DilaFooter = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Enter a valid email');
      return;
    }
    setStatus('loading');
    try {
      await subscribeNewsletter(email);
      setStatus('done');
      toast.success("You're on the list for the next Design Studio drop.");
    } catch (err) {
      setStatus('idle');
      toast.error('Something went wrong. Try again.');
    }
  };

  return (
    <footer className="mt-24 border-t-2 border-stone-900/40 pt-12" data-testid="dila-footer">
      {/* About D.I.L.A. */}
      <div className="max-w-3xl" data-testid="dila-footer-about">
        <div className="text-[10px] tracking-[0.4em] uppercase font-black mb-5">
          About D.I.L.A. — The Project
        </div>
        <div className="space-y-4 text-sm sm:text-base leading-relaxed">
          <p className="italic">
            Designed by Brandon Williamson with the help of A.I. Agents that assist in
            hyper-accelerating his operational bandwidth so he can stay in his creative flow state.
          </p>
          <p>
            Here at D.I.L.A., we don't participate in the overproduction cycle that is currently
            suffocating the apparel industry. We don't build massive, faceless warehouses full of
            generic inventory waiting for a markdown season.
          </p>
          <p className="font-black">
            Every single drop from DILA is engineered in highly disciplined, small-batch runs.
          </p>
          <p>
            Operating this way allows us to keep our environmental footprint exceptionally tight — we
            only pull raw textiles and mill yarn for the exact allocations we intend to ship. There is
            zero excess deadstock, zero landfill waste, and zero cutting-room compromise.
          </p>
          <p>
            The immediate byproduct of this operational discipline is total exclusivity for your
            wardrobe. When you are on the floor or out in the city, you are wearing an asset that was
            constructed with human intent, not industrial gluttony. Once a batch is cleared from the
            manifest, the formula resets — preventing exact replicas.
          </p>
          <p className="font-black tracking-[0.2em] uppercase text-xs pt-2 border-t border-stone-900/20">
            Conscious infrastructure. Elite rarity. From Napkin to Next Fit. Designed by DILA.
          </p>
        </div>
      </div>

      {/* Email catcher */}
      <div className="mt-14 max-w-xl" data-testid="dila-email-catcher">
        <div className="text-2xl sm:text-3xl font-black uppercase tracking-tight leading-tight mb-2">
          Hear About The Next Drop
        </div>
        <p className="text-sm leading-relaxed opacity-80 mb-5">
          The wash runs rarely. Leave your email to be first to know when the next D.I.L.A. Design
          Studio release goes live.
        </p>
        {status === 'done' ? (
          <div className="flex items-center gap-2 border-2 border-stone-900 bg-stone-900 text-amber-50 px-5 py-4 text-xs font-black tracking-[0.2em] uppercase" data-testid="dila-email-success">
            <Check className="w-4 h-4" /> You're on the list.
          </div>
        ) : (
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 border-2 border-stone-900 bg-white/40 backdrop-blur px-4 py-3.5 text-sm font-medium placeholder:text-stone-900/40 focus:bg-white/70 outline-none"
              data-testid="dila-email-input"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="flex items-center justify-center gap-2 bg-stone-900 text-amber-50 px-6 py-3.5 text-xs font-black tracking-[0.25em] uppercase hover:bg-stone-800 transition-colors disabled:opacity-50"
              data-testid="dila-email-submit"
            >
              {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Keep Me Posted
            </button>
          </form>
        )}
      </div>

      {/* Bottom line — stay in PRSVR */}
      <div className="mt-16 pt-8 border-t border-stone-900/30 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center text-[10px] tracking-[0.3em] uppercase font-black opacity-70">
        <span>Small Batch · Hand Finished · Maryland</span>
        <Link to="/dila" className="flex items-center gap-2 hover:opacity-100" data-testid="dila-shop-full-collection">
          Shop The Full Collection <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </footer>
  );
};

export default DilaFooter;
